// tabs/core-attacks.ts — client port of the Weapons tab's "Core Attacks" toolbar
//   options/game/character_sheet_display/weapons_equipped/_weapons_equipped_inc.php (Core Attacks block)
//
// Owner-only. Blob-resident: attack counters (encounter_creatures.weapon_attack/head_attack,
// extra_attack from computed.creature.attacks), opportunity-attack trigger button, and status-gated
// grapple/shove/disarm. All actions are existing template.js globals (render-only port).

import type { CachedCaster } from '../types';
import { renderSimpleTriggerAbilityButton, casterCanUseOpportunityAttack } from '../ability-button';
import { canShowDiveBomb, DIVE_BOMB_ATTACK_ABILITY_ID } from '../../shared/dive-bomb-visibility';
import { ftDispatchAbilityClick, ftLegacyAbilityClickAs } from './action-bar';

// status effect UUIDs (from the PHP)
const ST_GRAPPLED = '0C2A8037-B0BC-8300-C818-198881B00E7D';
const ST_RESTRAINED = 'E34AF232-5438-88FC-9A9F-73850CCCFD51';
const ST_GRAPPLING = 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD';
// ability UUIDs (from the PHP)
const AB_WEAPON_ATTACK_COUNT = '27D1EC95-4B26-CD9F-4101-109C35AC4430';
const AB_EXTRA_ATTACK = 'F8393F9C-EE0D-C457-4482-0F2C3D0E31EA';
const AB_HEAD_ATTACK = '32130B8E-BBAF-471A-B8F0-1D7C12E784AE';
const AB_OPPORTUNITY_ATTACK = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
const AB_SHOVE = '9AE1980E-783E-16F5-E8C4-904435FC0A69';
const AB_GRAPPLE = '6D6CCCC6-01F5-965C-FCF4-DBDF4DBCCD93';
const AB_GRAPPLE_TAKEDOWN = 'D8751A52-BC39-250E-7F6E-65B8028CC9FE';
const AB_GRAPPLE_RESTRAIN = '414E3282-1E29-44B5-95C9-7DA1D55EFCCB';
const AB_GRAPPLE_RELEASE = '3B98A7F7-1A94-4E3F-A85F-25BDC8430874';
const AB_GRAPPLE_ESCAPE = '98BE66E8-2F3D-DFEF-B4CD-DBE1EAA9ED4D';
const AB_GRAPPLE_RESTRAINED_BREAK = '2371CC7D-0BAD-DA78-7067-32361F5DDDBC';
const AB_DISARM = 'C6FD9254-7968-86FC-5FDC-8E87914DEDE4';
const SLOT_BOTH = 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D';

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}

/** has_status_effect twin: a status applied to the caster (target perspective). */
function statusHas(caster: CachedCaster, statusId: string): boolean {
    return ftCreatureStatuses(caster, 'target').some((e) => String(ftItemsCol(e, 'item_id') ?? '') === statusId);
}
/** creature_statuses(caster,'caster') emptiness — does the caster maintain any status? */
function maintainsAnyStatus(caster: CachedCaster): boolean {
    return ftCreatureStatuses(caster, 'caster').length > 0;
}
/** extra_attack bridges to computed.creature.attacks.extra_attack (scalar or {total}). */
function extraAttack(caster: CachedCaster): number {
    const slot = caster?.computed?.creature?.attacks?.extra_attack;
    if (slot !== undefined && slot !== null) {
        const total = slot && typeof slot === 'object' && !Array.isArray(slot)
            ? (slot as Record<string, unknown>).total
            : slot;
        return num(total);
    }
    return num(ftLayerValue(caster, 'extra_attack'));
}
/** Airborne or on high ground (the caster's underside sits above the grounded tolerance), and NOT
 *  hovering. physics_height is compile-stable; z_pos is live, so this stays fresh across moves.
 *  Janus enforces the real must-be-higher gate + fall at cast time — this only decides visibility. */
function feetOffGroundNotHovering(caster: CachedCaster): boolean {
    const ec = caster?.encounter_creatures as Record<string, unknown> | undefined;
    const posFn = window.ftPosCoord;
    const zCenter = (typeof posFn === 'function') ? num(posFn(caster, 'z_pos')) : 0;
    // This value is emitted directly at computed.creature.size.height. Do not ask the authored-layer
    // reader or substitute base size: Chara is 7.5 ft tall on a 5 ft base, so either path makes her
    // grounded center-Z (3.752) look airborne.
    const size = caster.computed?.creature?.size as Record<string, unknown> | undefined;
    const physHeight = num(size?.height);
    const preferredRaw = ec?.preferred_speed_type;
    const preferred = String((Array.isArray(preferredRaw) ? preferredRaw[0] : preferredRaw) ?? '').toUpperCase();
    return canShowDiveBomb(zCenter, physHeight, preferred);
}
/** Both-hands occupied? derive from inventory (any item equipped in the both_hands slot). */
function bothHandsEquipped(caster: CachedCaster): boolean {
    return ftLayerEntries(caster, 'inventory').some((e) => {
        const slot = String(ftLayerValue(e, 'equipped') ?? ftInventoryField(e, 'equipped') ?? '');
        return slot === SLOT_BOTH;
    });
}

function counterBtn(icon: string, abilityId: string, value: unknown, badgeVariant: string, extraAttrs = ''): string {
    return `<div class="p-1"><button type="button" class="btn btn-sm btn-dark border position-relative" data-ability_id="${abilityId}"${extraAttrs ? ' ' + extraAttrs : ''}>${iconMap(icon)}<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-${badgeVariant}">${escapeHtml(String(value ?? 0))}</span></button></div>`;
}
document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-core-attack-action="use-ability"]')
        : null;
    if (!el || !document.contains(el)) return;
    const casterId = el.dataset.ftCasterId ?? '';
    const abilityId = el.dataset.ftAbilityId ?? '';
    if (!casterId || !abilityId) return;
    event.preventDefault();
    if (!ftDispatchAbilityClick(casterId, abilityId)) ftLegacyAbilityClickAs(casterId, abilityId);
});

function actionBtn(icon: string, abilityId: string, casterId: string): string {
    const aid = escapeHtml(abilityId);
    const cid = escapeHtml(casterId);
    // Core attacks used to skip the FT3D targeting dispatcher entirely. Keep the legacy handler as
    // the non-FT3D/unsupported-profile fallback, but let directed attacks arm the shared intent first.
    return `<div class="p-1"><button type="button" class="btn btn-sm btn-info" data-ft-core-attack-action="use-ability" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}" data-ability_id="${aid}">${iconMap(icon)}</button></div>`;
}
function plainBtn(icon: string, cls: string, onclick: string): string {
    return `<div class="p-1"><button type="button" class="btn btn-sm ${cls}" onclick="${onclick}">${iconMap(icon)}</button></div>`;
}

export function renderCoreAttacks(caster: CachedCaster, casterId: string): string {
    if (!(typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster))) return '';

    const ec = caster?.encounter_creatures as Record<string, unknown> | undefined;
    const headWeapon = num(ftLayerValue(caster, 'count_head_weapon_equipped')) > 0;

    // ── counters group ──
    let counters = `<div class="ft-core-attack-group d-flex flex-wrap align-items-center gap-1">`;
    counters += counterBtn('weapon_attack_count', AB_WEAPON_ATTACK_COUNT, ec?.weapon_attack ?? 0, 'danger');
    // Extra Attack counter → opens the attack-budget breakdown (where each +attack comes from):
    // computed.creature.attacks.extra_attack {total, sources, breakdown}, via the character-description
    // delegated 'attacks-info' action → renderAttacksInfoPanel. title set for the hover affordance.
    counters += counterBtn('extra_attack', AB_EXTRA_ATTACK, extraAttack(caster), 'primary',
        `data-ft-pc-desc-action="attacks-info" data-ft-caster-id="${escapeHtml(casterId)}" title="Extra attacks — where they come from"`);
    if (headWeapon) counters += counterBtn('head_attack', AB_HEAD_ATTACK, ec?.head_attack ?? 0, 'secondary');
    counters += `</div>`;

    // ── add/reduce group ──
    let addReduce = `<div class="ft-core-attack-group d-flex flex-wrap align-items-center gap-1">`;
    addReduce += plainBtn('reduce_attack', 'btn-warning', 'reduceAttackToCaster();');
    addReduce += plainBtn('add_attack', 'btn-primary', 'addAttackToCaster();');
    if (headWeapon) {
        addReduce += plainBtn('reduce_head_attack', 'btn-warning', 'reduceHeadAttackToCaster();');
        addReduce += plainBtn('add_head_attack', 'btn-primary', 'addHeadAttackToCaster();');
    }
    addReduce += `</div>`;

    // ── actions group ──
    let actions = `<div class="ft-core-attack-group ft-core-attack-group--actions d-flex flex-wrap align-items-center gap-1">`;
    // Opportunity attack (trigger button, gated on a melee/natural weapon)
    const oaUsable = casterCanUseOpportunityAttack(caster);
    actions += `<div class="p-1">${renderSimpleTriggerAbilityButton(AB_OPPORTUNITY_ATTACK, 'btn btn-sm btn-info', iconMap('opportunity_attack'), caster, {
        title: oaUsable ? 'Opportunity Attack' : 'Opportunity Attack (requires a melee or natural weapon)',
        isDisabled: !oaUsable,
    })}</div>`;

    // Shove
    if (!maintainsAnyStatus(caster) || (!statusHas(caster, ST_GRAPPLED) && !statusHas(caster, ST_RESTRAINED))) {
        actions += actionBtn('shove', AB_SHOVE, casterId);
    }
    // Grapple (both hands free + not already grappling/grappled)
    if (!bothHandsEquipped(caster) && (!maintainsAnyStatus(caster) || (!statusHas(caster, ST_GRAPPLING) && !statusHas(caster, ST_GRAPPLED)))) {
        actions += actionBtn('grapple', AB_GRAPPLE, casterId);
    }
    // Advance grapple (caster IS grappling)
    if (statusHas(caster, ST_GRAPPLING)) {
        actions += actionBtn('advance_grapple_takedown', AB_GRAPPLE_TAKEDOWN, casterId);
        actions += actionBtn('advance_grapple_restrain', AB_GRAPPLE_RESTRAIN, casterId);
        actions += actionBtn('release_grapple', AB_GRAPPLE_RELEASE, casterId);
    }
    // Regress grapple (caster IS grappled)
    if (statusHas(caster, ST_GRAPPLED)) {
        if (!statusHas(caster, ST_RESTRAINED)) actions += actionBtn('regress_grapple_escape', AB_GRAPPLE_ESCAPE, casterId);
        if (statusHas(caster, ST_RESTRAINED)) actions += actionBtn('regress_grapple_restrained', AB_GRAPPLE_RESTRAINED_BREAK, casterId);
    }
    // Disarm (always)
    actions += actionBtn('disarm', AB_DISARM, casterId);
    // Dive Bomb — airborne or on high ground, not hovering (mirrors the action-bar conditional).
    if (feetOffGroundNotHovering(caster)) actions += actionBtn('caster_dive_bombs_target', DIVE_BOMB_ATTACK_ABILITY_ID, casterId);
    actions += `</div>`;

    return `<div class="ft-pc-core-attacks">
        <h5 class="ft-weapons-tab-h ft-ability-hdr text-start ps-3 mt-1 mb-0 py-2 rounded-top" style="--ft-ah:#7aa2ff;">Core Attacks</h5>
        <div class="row m-0 mb-3 ft-core-attacks">
            <div class="col-12 d-flex flex-wrap align-items-start gap-2 gap-lg-3 px-3 pt-2 pb-1">
                ${counters}${addReduce}${actions}
            </div>
        </div>
    </div>`;
}
